// Discovery tracker — Rogers screen 04 (payment_spec.md "Discovery tracker" contract).
// tracker_daily is a cumulative time series meant to be PLOTTED, not a latest-value
// snapshot: it keeps rows all year, so `series` below covers the whole year (mkt_*/harv_*
// columns) while `windowSeries` isolates the discovery-window-only proj_* rows
// (proj_day_number IS NOT NULL). `latest` is the is_latest row — it sits OUTSIDE the
// window with a null day number and carries the settled basis + official price; reading
// the basis from the day-filtered set alone would frame a closed window as still forming.

import { roundHalfUp } from "./mcoModel.js";
import { num } from "./mcoShared.js";

const round2 = (v) => roundHalfUp(v, 2);

// Discovery period for the fall-sold crops this POC ships: 30 days from Aug 15, in the calendar
// year BEFORE the reinsurance year — RY2026 discovery ran Aug 15 - Sep 14, 2025, because sales
// close Sep 30 ahead of the crop year. Anchoring on the data's own dates instead would put a
// closed RY2026 window in Aug 2026 and read it as freshly open.
// ponytail: per-crop RMA windows are already served per price leg (projectedWindow on priceLegs);
// swap these constants for that pair when a spring-discovery crop reaches this view.
export const DISCOVERY_PERIOD_START = "08-15";
export const DISCOVERY_PERIOD_DAYS = 30;

const DAY_MS = 86400000;
const PRE_PERIOD_LOOKBACK_DAYS = 45;

// Local-midnight ms: new Date("YYYY-MM-DD") parses as UTC and lands a day early west of Greenwich.
const dateMs = (iso) => new Date(`${String(iso).slice(0, 10)}T00:00:00`).getTime();

/**
 * Visible date window for the discovery tracker. The period is fixed by the reinsurance year; the
 * as-of date (newest observation, not the wall clock, so a lagging mirror still fills the chart)
 * only decides which window is worth looking at:
 *   before the period opens — the 45 days up to asOf (there is no window to show yet)
 *   during or after         — the period itself, so days settled / days left read off the axis
 */
export function discoveryWindow(reinsuranceYear, asOfDate) {
  const periodStart = dateMs(`${num(reinsuranceYear) - 1}-${DISCOVERY_PERIOD_START}`);
  const periodEnd = periodStart + DISCOVERY_PERIOD_DAYS * DAY_MS;
  const asOf = dateMs(asOfDate);
  const periodOpen = asOf >= periodStart;
  return {
    start: periodOpen ? periodStart : asOf - PRE_PERIOD_LOOKBACK_DAYS * DAY_MS,
    end: periodOpen ? periodEnd : asOf,
    periodOpen,
    periodStart,
    periodEnd,
    asOf,
  };
}

/** Whole days from the as-of date to the period's close — 0 once the window has shut, never negative. */
export const daysToClose = (win) => Math.max(0, Math.round((win.periodEnd - win.asOf) / DAY_MS));

export const withinWindow = (win, isoDate) => {
  const t = dateMs(isoDate);
  return t >= win.start && t <= win.end;
};

/**
 * Zoomed quantitative domain: data min/max plus padding. Margin moves of a few dollars an acre
 * are the whole point of this chart and vanish on a zero-based axis. Returns null for no data —
 * never a made-up [0, 1].
 */
export function paddedDomain(values, padFraction = 0.08) {
  const nums = values.filter((v) => Number.isFinite(v));
  if (!nums.length) return null;
  const min = Math.min(...nums);
  const max = Math.max(...nums);
  const pad = (max - min) * padFraction || Math.abs(max) * padFraction || 1;
  return [min - pad, max + pad];
}

/**
 * County per-acre bridge (payment_spec.md "County per-acre bridge", named formula):
 * EAY x margin_bu - fixed_acre. fixed_acre is a flat per-acre term subtracted AFTER the
 * EAY multiply, never multiplied by EAY — the documented foot-gun. A null margin or a
 * missing/zero EAY returns null ("not yet determined"), never a $0 guarantee.
 */
export function bridgeToPerAcre(marginBu, fixedAcre, eay) {
  if (marginBu == null || eay == null || !(eay > 0)) return null;
  return round2(eay * num(marginBu) - num(fixedAcre));
}

/**
 * The two halves of the bridged margin, per acre: revenue is EAY x price, cost is EAY x cost_bu
 * PLUS the flat fixed_acre term (the same term the bridge subtracts). They reconcile exactly —
 * revenuePerAcre - costPerAcre === bridgeToPerAcre(price - cost) — so a $/ac chart can draw price
 * and margin as lines with cost as the gap between them, on ONE axis, exactly as the per-bushel
 * reference tracker does.
 */
export function revenuePerAcre(priceBu, eay) {
  if (priceBu == null || eay == null || !(eay > 0)) return null;
  return round2(eay * num(priceBu));
}

export function costPerAcre(costBu, fixedAcre, eay) {
  if (costBu == null || eay == null || !(eay > 0)) return null;
  return round2(eay * num(costBu) + num(fixedAcre));
}

// Per-leg cost split. The mirror serves the native input prices and the cost TOTAL, never the
// split, so the split derives from the engine's quantity slopes — documented constants on the SWE
// contract (mco_eco_app_reference/notebooks/04_tracker.py; products/mco/config.INPUT_QUANTITIES,
// transcribed from the Watts "MCO <Commodity> Input" sheets, Fall 2025).
//
// qty(EAY) = slope x EAY + intercept, so per ACRE cost is (slope x EAY + intercept) x price and
// per BUSHEL it is slope x price + intercept x price / EAY. The slope term is therefore yield-free
// — exact from day one, which is why this screen is per bushel — and the intercept term is the
// flat per-acre diesel charge the mirror already serves as proj_fixed_acre (not re-derived here).
//
// Baskets differ by commodity and practice: soybeans carry NO urea (a legume fixes its own N), and
// natural gas is an irrigated-practice leg only.
const LBS_PER_TON = 2000;

// leg -> [slope per yield unit, priced in $/ton (lbs/acre quantity)]
const INPUT_QUANTITIES = {
  corn: {
    urea: [0.53 / 0.46, true],
    dap: [0.35 / 0.46, true],
    potash: [0.25 / 0.6, true],
    naturalGas: [0.04, false],
    diesel: [0.04, false],
  },
  soybeans: {
    dap: [0.73 / 0.46, true],
    potash: [1.1 / 0.6, true],
    naturalGas: [0.14, false],
    diesel: [0.1, false],
  },
};

const IRRIGATED_ONLY = ["naturalGas"];

// Anything not "non-irrigated" that still mentions irrigation is the irrigated basket. An
// unrecognized practice falls back to the smaller basket and the reconciliation below catches it:
// a missing leg blows the gap open, which hides the split rather than showing a wrong one.
const isIrrigated = (practiceName) => /irrigat/i.test(practiceName || "") && !/non/i.test(practiceName || "");

/** The commodity/practice cost basket as [{ leg, slope, perTon }] — empty when unsupported. */
export function costBasket(commodityName, practiceName) {
  const table =
    INPUT_QUANTITIES[
      String(commodityName || "")
        .trim()
        .toLowerCase()
    ];
  if (!table) return [];
  const irrigated = isIrrigated(practiceName);
  return Object.entries(table)
    .filter(([leg]) => irrigated || !IRRIGATED_ONLY.includes(leg))
    .map(([leg, [slope, perTon]]) => ({ leg, slope, perTon }));
}

/**
 * Per-bushel contribution of each basket leg at one day's native prices. Null when any leg's price
 * is missing — a partial split would read as a complete one and under-state the cost.
 */
export function costContributions(nativePrices, basket) {
  if (!basket?.length) return null;
  const out = {};
  for (const { leg, slope, perTon } of basket) {
    const price = nativePrices?.[leg];
    if (price == null) return null;
    out[leg] = roundHalfUp((slope * num(price)) / (perTon ? LBS_PER_TON : 1), 4);
  }
  return out;
}

/**
 * Cent allocation, largest-remainder. Contributions are carried at 4dp; rounding each to cents
 * independently makes the displayed legs sum to a penny more than the displayed total
 * ($0.55 + $0.54 + $0.22 against $1.30). Give the odd cents to the largest remainders so what is
 * on screen adds up. Drift still compares the raw 4dp values.
 */
export function allocateCents(values, total) {
  const scaled = values.map((v) => num(v) * 100);
  const out = scaled.map(Math.floor);
  const byRemainder = scaled.map((v, i) => ({ i, frac: v - Math.floor(v) })).sort((a, b) => b.frac - a.frac);
  let diff = Math.round(num(total) * 100) - out.reduce((a, b) => a + b, 0);
  for (let k = 0; diff > 0 && k < byRemainder.length; k++, diff--) out[byRemainder[k].i] += 1;
  for (let k = byRemainder.length - 1; diff < 0 && k >= 0; k--, diff++) out[byRemainder[k].i] -= 1;
  return out.map((c) => c / 100);
}

// Over this much per bushel the derived split no longer matches the engine's cost definition, so
// the screen hides it rather than showing legs that don't add up to the total beside them.
export const COST_SPLIT_TOLERANCE_BU = 0.005;

function toSeriesRow(r) {
  return {
    date: String(r.trade_date ?? r.tradeDate ?? "").slice(0, 10),
    day: r.proj_day_number != null ? num(r.proj_day_number) : null,
    projMarginBu: r.proj_margin_bu != null ? num(r.proj_margin_bu) : null,
    projPriceBu: r.proj_price != null ? num(r.proj_price) : null,
    projCostBu: r.proj_cost_bu != null ? num(r.proj_cost_bu) : null,
    projFixedAcre: r.proj_fixed_acre != null ? num(r.proj_fixed_acre) : null,
    projTriggerSpreadBu: r.proj_trigger_spread_bu != null ? num(r.proj_trigger_spread_bu) : null,
    projBasis: r.proj_basis ?? null,
    projOfficialPrice: r.proj_official_price != null ? num(r.proj_official_price) : null,
    mktMarginBu: r.mkt_margin_bu != null ? num(r.mkt_margin_bu) : null,
    mktPriceBu: r.mkt_price != null ? num(r.mkt_price) : null,
    mktCostBu: r.mkt_cost_bu != null ? num(r.mkt_cost_bu) : null,
    harvMarginBu: r.harv_margin_bu != null ? num(r.harv_margin_bu) : null,
    harvPriceCapped: r.harv_price_capped != null ? num(r.harv_price_capped) : null,
    harvPriceUncapped: r.harv_price_uncapped != null ? num(r.harv_price_uncapped) : null,
    harvCostBu: r.harv_cost_bu != null ? num(r.harv_cost_bu) : null,
    harvState: r.harv_state ?? null,
    legs: {
      diesel: r.diesel_price != null ? num(r.diesel_price) : null,
      urea: r.urea_price != null ? num(r.urea_price) : null,
      dap: r.dap_price != null ? num(r.dap_price) : null,
      naturalGas: r.ng_price != null ? num(r.ng_price) : null,
      potash: r.potash_price != null ? num(r.potash_price) : null,
    },
    potashStatus: r.potash_status ?? null,
    basisNote: r.basis_note ?? null,
    isLatest: r.is_latest === true || r.is_latest === 1,
  };
}

/**
 * @param {object[]} rows — mirror TrackerDaily rows (one crop_year/commodity/practice,
 *   already filtered to is_default_provenance = 1 server-side)
 * @param {object} [opts]
 * @param {number} [opts.eay] — expected area yield, for the per-acre bridge
 * @param {string} [opts.eayBasis="official"] — disclose which of the three bridge sources
 *   (payment_spec.md: official / model_forecast / hypothetical) `eay` came from
 * @param {number} [opts.reinsuranceYear] — fixes the discovery period, which sits in the prior
 *   calendar year (see discoveryWindow); the rows' own trade dates span it and can't stand in
 * @param {string} [opts.commodityName] — picks the cost basket for the per-leg split (costBasket)
 * @param {string} [opts.practiceName] — irrigated practices carry the extra natural-gas leg
 */
export function buildMcoTrackerReport(
  rows,
  { eay, eayBasis = "official", reinsuranceYear = null, commodityName = null, practiceName = null } = {}
) {
  const list = Array.isArray(rows) ? rows : [];
  if (!list.length) {
    return {
      ready: false,
      blockers: ["No tracker data for this crop/practice/year"],
      series: [],
      windowSeries: [],
      latest: null,
    };
  }

  const series = list.map(toSeriesRow).sort((a, b) => (a.date < b.date ? -1 : a.date > b.date ? 1 : 0));

  // Every day's derived split is reconciled against the tracker's own proj_cost_bu; the worst gap
  // rides on the report so a drift surfaces instead of hiding behind legs that look authoritative.
  const basket = costBasket(commodityName, practiceName);
  let costSplitGap = 0;
  let anySplit = false;
  for (const row of series) {
    row.contribs = costContributions(row.legs, basket);
    if (row.contribs && row.projCostBu != null) {
      anySplit = true;
      const summed = Object.values(row.contribs).reduce((a, b) => a + b, 0);
      costSplitGap = Math.max(costSplitGap, Math.abs(summed - row.projCostBu));
    }
  }

  const windowSeries = series.filter((r) => r.day != null).sort((a, b) => a.day - b.day);
  const latest = series.find((r) => r.isLatest) || series[series.length - 1] || null;

  const settled = Boolean(latest && (latest.projBasis === "locked" || latest.projBasis === "official"));
  const lockedMarginBu = settled ? latest.projMarginBu : null;
  const fixedAcre = latest?.projFixedAcre ?? null;
  // ponytail: the bridge formula is spec'd for "the tracker's per-bushel margin, matching
  // the quantity displayed" — treated here as quantity-agnostic, so the trigger spread
  // bridges the same way as margin_bu. No vector currently pins a trigger-side bridge
  // example; upgrade path is a dedicated CI vector if payment_test_vectors adds one.
  const triggerSpreadBu = latest?.projTriggerSpreadBu ?? null;
  const marketMarginBu = latest?.mktMarginBu ?? null;
  const harvestMarginBu = latest?.harvMarginBu ?? null;

  const eayNum = eay != null ? num(eay) : null;
  const eayAvailable = eayNum != null && eayNum > 0;

  return {
    ready: true,
    blockers: [],
    reinsuranceYear: reinsuranceYear != null ? num(reinsuranceYear) : null,
    windowDayCount: windowSeries.length ? windowSeries[windowSeries.length - 1].day : 0,
    settled,
    basis: latest?.projBasis ?? null,
    harvestState: latest?.harvState ?? null,
    officialPrice: latest?.projOfficialPrice ?? null,
    basket,
    // Rendering the split is gated on this: legs that don't add up to the total beside them are
    // worse than no legs at all.
    costSplitReady: anySplit && costSplitGap <= COST_SPLIT_TOLERANCE_BU,
    costSplitGap: roundHalfUp(costSplitGap, 4),
    series,
    windowSeries,
    latest,
    perBushel: { lockedMarginBu, triggerSpreadBu, marketMarginBu, harvestMarginBu },
    perAcre: {
      eayAvailable,
      eayBasis: eayAvailable ? eayBasis : null,
      eay: eayAvailable ? eayNum : null,
      fixedAcre,
      expectedMarginPerAcre: eayAvailable ? bridgeToPerAcre(lockedMarginBu, fixedAcre, eayNum) : null,
      triggerPerAcre: eayAvailable ? bridgeToPerAcre(triggerSpreadBu, fixedAcre, eayNum) : null,
      harvestMarginEstimatePerAcre: eayAvailable ? bridgeToPerAcre(harvestMarginBu, fixedAcre, eayNum) : null,
    },
  };
}
